/** Executable research records contain proposals and observations, never source
 * admission. This module is also bundled into the standalone runner so imports,
 * the UI and headless execution use the same contract. */
import type { ExperimentReviewOptions } from './experimentReview'
import { fnv1a64 } from './similarity'
import {
  createDecisionSelector,
  parseDecisionModel,
  type DecisionModel,
  type DecisionRanking,
} from './decisionCertificates'

export const EXPERIMENT_VERSION = 1 as const
export const EXPERIMENT_LIMITS = {
  stateBytes: 8_000_000,
  artifactBytes: 512_000,
  programBytes: 64_000,
  artifacts: 256,
  experiments: 64,
  receipts: 128,
  seeds: 16,
  outputBytes: 64_000,
  wallMs: 30_000,
  memoryMb: 256,
  cpuSeconds: 20,
} as const
export type ArtifactKind = 'dataset' | 'code' | 'evaluator' | 'evidence' | 'method' | 'claim'
export interface ResearchArtifact {
  id: string
  kind: ArtifactKind
  content: string
  scope: string
  provenance: 'authored-example' | 'user-supplied' | 'model-proposed'
  dependsOn: { id: string; revision: string }[]
  freshness: { kind: 'immutable' } | { kind: 'web'; checkedAt: number; maxAgeMs: number }
}
export interface ExecutableHypothesis {
  id: string
  mechanism: string
  conditions: string[]
  prediction: { metric: string; minimum: number; maximum: number }
  counterEvidence: string
}
export interface ExperimentSpec {
  version: 1
  id: string
  title: string
  question: string
  context: {
    sessionId: string | null
    nodeId: string | null
    claim: string | null
    testId: string | null
  }
  hypotheses: ExecutableHypothesis[]
  otherExplanation: string
  intervention: { variable: string; change: string; controls: string[] }
  candidate: { language: 'javascript' | 'python'; artifactId: string }
  evaluator: {
    language: 'javascript' | 'python'
    artifactId: string
    authority: 'task-author' | 'unreviewed-proposal'
  }
  dataId: string
  evaluatorDataId: string
  /** Bound explicitly on the execution host before dispatch. Null is a proposal. */
  environment: null | {
    platform: string
    release: string
    arch: string
    isolation: 'macos-sandbox' | 'docker'
    runnerDigest: string
    runtimes: Record<string, string>
  }
  /** All consumed material must appear here, including methods and related claims. */
  inputs: { id: string; revision: string }[]
  seeds: number[]
  metric: { name: string; unit: string; aggregation: 'mean'; minimum: number; maximum: number }
  limits: { wallMs: number; cpuSeconds: number; memoryMb: number; outputBytes: number }
}
export interface ProcessObservation {
  exitCode: number | null
  signal: string | null
  stdout: string
  stderr: string
  wallMs: number
  limited: boolean
  /** Only the runner's mechanical observations can name a limit. Old receipts
   * deliberately leave the cause unknown; stderr is never a classifier. */
  limitReason?: 'wall-time' | 'cpu-time' | 'memory' | 'output' | 'cancelled' | 'monitor-failed'
  peakRssKb?: number
}
export interface ExperimentReceipt {
  version: 1
  id: string
  experimentId: string
  spec: ExperimentSpec
  artifacts: ResearchArtifact[]
  /** SHA-256 covers the canonical spec and retained artifacts. It detects changes,
   * not who executed the package or whether its claims are scientifically true. */
  inputDigest: string
  startedAt: number
  completedAt: number
  isolation: {
    kind: 'macos-sandbox' | 'docker'
    runtime: string
    platform: string
    runnerDigest: string
  }
  runs: { seed: number; candidate: ProcessObservation; evaluator: ProcessObservation | null }[]
}
export interface MechanismTransfer {
  id: string
  sourceArtifactId: string
  targetExperimentId: string
  sourceMechanism: string
  mapping: {
    source: string
    target: string
    sourceUnit: string
    targetUnit: string
    transform: string
  }[]
  preserved: string[]
  changedConditions: string[]
  failureTest: string
}
export interface ExperimentAttempt {
  id: string
  experimentId: string
  startedAt: number
  inputKey: string
  input: { spec: ExperimentSpec; artifacts: ResearchArtifact[] }
  state: 'exported' | 'completed' | 'unknown' | 'abandoned'
  receiptId: string | null
  transport?: 'paired-local'
  /** Frozen diagnostic intent accompanies the native input; it is not a verdict. */
  mathematics?: {
    plan: { id: string; revision: string }
    target: { id: string; revision: string }
    package: { id: string; revision: string }
    experimentRevision: string
  }
}
export interface ExperimentRevision {
  maxTokens?: number
  review?: ExperimentReviewOptions
  continuationOf?: string
  id: string
  receiptId: string
  nodeId: string
  claim: string
  startedAt: number
  opId: string | null
  state: 'pending' | 'completed' | 'unknown' | 'refused'
  produced: string[]
}
export interface ExperimentWorkspaceState {
  version: 1
  draft: string
  artifacts: ResearchArtifact[]
  experiments: ExperimentSpec[]
  receipts: ExperimentReceipt[]
  attempts: ExperimentAttempt[]
  transfers: MechanismTransfer[]
  revisions?: ExperimentRevision[]
  /** Navigation references, not evidence admission or permission to rerun. */
  links?: {
    sessionId: string
    title: string
    relation: 'source' | 'companion'
  }[]
  ranking?: {
    model: DecisionModel
    /** Exact frozen packages, not a short hash, bind each declared partition. */
    bindings: ExperimentAttempt['input'][]
  } | null
}

const obj = (v: unknown): v is Record<string, unknown> =>
  !!v &&
  typeof v === 'object' &&
  !Array.isArray(v) &&
  (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null)
const str = (v: unknown, max = 4000): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= max
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const natural = (v: unknown): v is number => num(v) && Number.isSafeInteger(v) && v >= 0
const list = (v: unknown, min = 0, max = 64): v is unknown[] =>
  Array.isArray(v) && v.length >= min && v.length <= max
const texts = (v: unknown, min = 0) => list(v, min) && v.every((x) => str(x))
const id = (v: unknown): v is string => str(v, 160) && /^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/.test(v)
const nullable = (v: unknown) => v === null || str(v, 20000)
const unique = (values: readonly string[]) => new Set(values).size === values.length
const bytes = (v: string) => new TextEncoder().encode(v).byteLength

/** Reject accessors, non-JSON values and oversized nested data before traversing
 * imported records. Canonical bytes are shared with the Node SHA-256 boundary. */
export function experimentCanonical(value: unknown): string {
  let count = 0
  const walk = (v: unknown, depth: number): string => {
    if (++count > 200000 || depth > 32)
      throw new Error('Research package exceeds structural limits.')
    if (v === null || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)))
      return JSON.stringify(v)
    if (typeof v === 'string') {
      if (bytes(v) > EXPERIMENT_LIMITS.artifactBytes)
        throw new Error('Research text exceeds its byte limit.')
      return JSON.stringify(v)
    }
    if (Array.isArray(v) || obj(v)) {
      if (
        Object.getOwnPropertySymbols(v).length ||
        Object.values(Object.getOwnPropertyDescriptors(v)).some((d) => d.get || d.set)
      )
        throw new Error('Research records must be plain JSON data.')
      if (Array.isArray(v)) {
        if (Object.keys(v).length !== v.length)
          throw new Error('Sparse arrays are not research data.')
        return `[${v.map((x) => walk(x, depth + 1)).join(',')}]`
      }
      return `{${Object.keys(v)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${walk(v[key], depth + 1)}`)
        .join(',')}}`
    }
    throw new Error('Research records must be finite JSON data.')
  }
  const result = walk(value, 0)
  if (bytes(result) > EXPERIMENT_LIMITS.stateBytes)
    throw new Error('Research package exceeds its byte limit.')
  return result
}
const copy = <T>(v: T): T => JSON.parse(experimentCanonical(v))
const refs = (v: unknown) =>
  list(v, 0, EXPERIMENT_LIMITS.artifacts) &&
  v.every((r) => obj(r) && id(r.id) && str(r.revision, 100)) &&
  unique(v.map((r) => (r as { id: string }).id))

export function parseResearchArtifact(v: unknown): ResearchArtifact | null {
  if (
    !obj(v) ||
    !id(v.id) ||
    !['dataset', 'code', 'evaluator', 'evidence', 'method', 'claim'].includes(v.kind as string) ||
    !str(v.content, EXPERIMENT_LIMITS.artifactBytes) ||
    bytes(v.content as string) > EXPERIMENT_LIMITS.artifactBytes ||
    !str(v.scope) ||
    !['authored-example', 'user-supplied', 'model-proposed'].includes(v.provenance as string) ||
    !refs(v.dependsOn) ||
    !obj(v.freshness)
  )
    return null
  if (
    (v.kind === 'code' || v.kind === 'evaluator') &&
    bytes(v.content) > EXPERIMENT_LIMITS.programBytes
  )
    return null
  if (
    v.freshness.kind !== 'immutable' &&
    (v.freshness.kind !== 'web' ||
      !natural(v.freshness.checkedAt) ||
      !natural(v.freshness.maxAgeMs) ||
      v.freshness.maxAgeMs === 0)
  )
    return null
  return copy(v) as unknown as ResearchArtifact
}

/** A local change detector. Exact retained package comparison is also required
 * before reuse, so a hash collision cannot authorize reuse on different input. */
export const artifactRevision = (artifact: ResearchArtifact): string =>
  `fnv1a64:${fnv1a64(experimentCanonical(artifact))}`

export function parseExperimentSpec(v: unknown): ExperimentSpec | null {
  if (
    !obj(v) ||
    v.version !== 1 ||
    !id(v.id) ||
    !str(v.title) ||
    !str(v.question, 20000) ||
    !obj(v.context) ||
    !['sessionId', 'nodeId', 'claim', 'testId'].every((k) =>
      nullable(v.context && (v.context as Record<string, unknown>)[k]),
    ) ||
    !list(v.hypotheses, 2, 16) ||
    !str(v.otherExplanation) ||
    !obj(v.intervention) ||
    !str(v.intervention.variable) ||
    !str(v.intervention.change) ||
    !texts(v.intervention.controls, 1) ||
    !obj(v.candidate) ||
    !obj(v.evaluator) ||
    !['javascript', 'python'].includes(v.candidate.language as string) ||
    !['javascript', 'python'].includes(v.evaluator.language as string) ||
    !id(v.candidate.artifactId) ||
    !id(v.evaluator.artifactId) ||
    v.candidate.artifactId === v.evaluator.artifactId ||
    !['task-author', 'unreviewed-proposal'].includes(v.evaluator.authority as string) ||
    !id(v.dataId) ||
    !id(v.evaluatorDataId) ||
    v.dataId === v.evaluatorDataId ||
    !refs(v.inputs) ||
    !list(v.seeds, 1, EXPERIMENT_LIMITS.seeds) ||
    !v.seeds.every((n) => natural(n) && n <= 2147483647) ||
    !unique(v.seeds.map(String)) ||
    !obj(v.metric) ||
    !id(v.metric.name) ||
    !str(v.metric.unit, 100) ||
    v.metric.aggregation !== 'mean' ||
    !num(v.metric.minimum) ||
    !num(v.metric.maximum) ||
    v.metric.minimum > v.metric.maximum ||
    !obj(v.limits)
  )
    return null
  for (const h of v.hypotheses) {
    if (
      !obj(h) ||
      !id(h.id) ||
      !str(h.mechanism) ||
      !texts(h.conditions, 1) ||
      !str(h.counterEvidence) ||
      !obj(h.prediction) ||
      h.prediction.metric !== v.metric.name ||
      !num(h.prediction.minimum) ||
      !num(h.prediction.maximum) ||
      h.prediction.minimum > h.prediction.maximum
    )
      return null
  }
  if (!unique(v.hypotheses.map((h) => (h as { id: string }).id))) return null
  if (v.environment !== null) {
    const e = v.environment
    if (
      !obj(e) ||
      !str(e.platform) ||
      !str(e.release) ||
      !str(e.arch) ||
      typeof e.runnerDigest !== 'string' ||
      !/^[a-f0-9]{64}$/.test(e.runnerDigest) ||
      !['macos-sandbox', 'docker'].includes(e.isolation as string) ||
      !obj(e.runtimes) ||
      ![v.candidate.language, v.evaluator.language].every(
        (language) =>
          typeof e.runtimes === 'object' &&
          /^sha256:[a-f0-9]{64}$/.test(
            String((e.runtimes as Record<string, unknown>)[String(language)]),
          ),
      )
    )
      return null
  }
  for (const key of ['wallMs', 'cpuSeconds', 'memoryMb', 'outputBytes'] as const) {
    if (!natural(v.limits[key]) || v.limits[key] === 0 || v.limits[key] > EXPERIMENT_LIMITS[key])
      return null
  }
  const required = [v.candidate.artifactId, v.evaluator.artifactId, v.dataId, v.evaluatorDataId]
  if (!required.every((key) => (v.inputs as { id: string }[]).some((r) => r.id === key)))
    return null
  return copy(v) as unknown as ExperimentSpec
}

function processValid(v: unknown): boolean {
  return (
    obj(v) &&
    (v.exitCode === null || natural(v.exitCode)) &&
    (v.signal === null || str(v.signal, 80)) &&
    typeof v.stdout === 'string' &&
    bytes(v.stdout) <= EXPERIMENT_LIMITS.outputBytes &&
    typeof v.stderr === 'string' &&
    bytes(v.stderr) <= EXPERIMENT_LIMITS.outputBytes &&
    num(v.wallMs) &&
    v.wallMs >= 0 &&
    typeof v.limited === 'boolean' &&
    (v.limitReason === undefined ||
      (v.limited === true &&
        ['wall-time', 'cpu-time', 'memory', 'output', 'cancelled', 'monitor-failed'].includes(
          v.limitReason as string,
        ))) &&
    (v.peakRssKb === undefined || natural(v.peakRssKb))
  )
}

export function parseExperimentReceipt(value: unknown): ExperimentReceipt | null {
  try {
    experimentCanonical(value)
    if (
      !obj(value) ||
      value.version !== 1 ||
      !id(value.id) ||
      !id(value.experimentId) ||
      !parseExperimentSpec(value.spec) ||
      (value.spec as ExperimentSpec).id !== value.experimentId ||
      !list(value.artifacts, 4, EXPERIMENT_LIMITS.artifacts) ||
      !value.artifacts.every((a) => parseResearchArtifact(a)) ||
      !unique(value.artifacts.map((a) => (a as ResearchArtifact).id)) ||
      typeof value.inputDigest !== 'string' ||
      !/^[a-f0-9]{64}$/.test(value.inputDigest) ||
      !natural(value.startedAt) ||
      !natural(value.completedAt) ||
      value.completedAt < value.startedAt ||
      !obj(value.isolation) ||
      !['macos-sandbox', 'docker'].includes(value.isolation.kind as string) ||
      !str(value.isolation.runtime) ||
      !str(value.isolation.platform) ||
      typeof value.isolation.runnerDigest !== 'string' ||
      !/^[a-f0-9]{64}$/.test(value.isolation.runnerDigest) ||
      !list(value.runs, 0, EXPERIMENT_LIMITS.seeds)
    )
      return null
    const spec = value.spec as ExperimentSpec
    if (
      !value.runs.every(
        (r, i) =>
          obj(r) &&
          r.seed === spec.seeds[i] &&
          processValid(r.candidate) &&
          (r.evaluator === null || processValid(r.evaluator)),
      )
    )
      return null
    return copy(value) as unknown as ExperimentReceipt
  } catch {
    return null
  }
}

export function experimentInputIssues(
  spec: ExperimentSpec,
  artifacts: readonly ResearchArtifact[],
  now: number,
): string[] {
  const byId = new Map(artifacts.map((a) => [a.id, a]))
  const issues: string[] = []
  const visiting = new Set<string>(),
    visited = new Set<string>()
  const walk = (reference: { id: string; revision: string }) => {
    const a = byId.get(reference.id)
    if (!a) {
      issues.push(`Missing input: ${reference.id}`)
      return
    }
    if (artifactRevision(a) !== reference.revision) issues.push(`Changed input: ${a.id}`)
    if (
      a.freshness.kind === 'web' &&
      (now < a.freshness.checkedAt || now - a.freshness.checkedAt >= a.freshness.maxAgeMs)
    )
      issues.push(`Web input needs refresh: ${a.id}`)
    if (visiting.has(a.id)) {
      issues.push(`Cyclic dependency: ${a.id}`)
      return
    }
    if (visited.has(a.id)) return
    visiting.add(a.id)
    for (const r of a.dependsOn) {
      if (!spec.inputs.some((input) => input.id === r.id))
        issues.push(`Undeclared transitive input: ${r.id}`)
      walk(r)
    }
    visiting.delete(a.id)
    visited.add(a.id)
  }
  for (const r of spec.inputs) walk(r)
  for (const [key, kind] of [
    [spec.candidate.artifactId, 'code'],
    [spec.evaluator.artifactId, 'evaluator'],
    [spec.dataId, 'dataset'],
    [spec.evaluatorDataId, 'dataset'],
  ] as const)
    if (byId.get(key)?.kind !== kind) issues.push(`Wrong input kind: ${key} must be ${kind}`)
  return [...new Set(issues)]
}

export function experimentInput(spec: ExperimentSpec, artifacts: readonly ResearchArtifact[]) {
  return {
    spec,
    artifacts: spec.inputs
      .map((r) => artifacts.find((a) => a.id === r.id))
      .filter((a): a is ResearchArtifact => !!a)
      .sort((a, b) => a.id.localeCompare(b.id)),
  }
}
export const experimentInputKey = (spec: ExperimentSpec, artifacts: readonly ResearchArtifact[]) =>
  `fnv1a64:${fnv1a64(experimentCanonical(experimentInput(spec, artifacts)))}`

/** Reserve retained input copies and the declared output allowance BEFORE a
 * handoff. One byte can become six JSON bytes (a control character); both
 * streams of both processes travel in every seed's receipt. The metadata
 * margin also covers an explicitly bound environment on an exported proposal.
 * This is a storage reserve, not a prediction of output size or scientific value. */
const RECEIPT_METADATA_RESERVE = 64_000
const receiptReserve = (input: ExperimentAttempt['input']) =>
  bytes(experimentCanonical(input)) +
  RECEIPT_METADATA_RESERVE +
  input.spec.seeds.length * (4 * 6 * input.spec.limits.outputBytes + 1024)

export function experimentRetentionCapacity(
  state: ExperimentWorkspaceState,
  proposed?: ExperimentAttempt['input'],
) {
  const usedBytes = bytes(experimentCanonical(state))
  const pending = state.attempts.filter((a) => ['exported', 'unknown'].includes(a.state))
  const pendingReceiptBytes = pending.reduce((sum, a) => sum + receiptReserve(a.input), 0)
  const attemptBytes = proposed
    ? bytes(experimentCanonical(proposed)) + RECEIPT_METADATA_RESERVE
    : 0
  const receiptBytes = proposed ? receiptReserve(proposed) : 0
  const projectedBytes = usedBytes + pendingReceiptBytes + attemptBytes + receiptBytes
  const issues: string[] = []
  if (projectedBytes > EXPERIMENT_LIMITS.stateBytes)
    issues.push(
      `Retaining this work reserves ${projectedBytes.toLocaleString('en-US')} bytes against the ${EXPERIMENT_LIMITS.stateBytes.toLocaleString('en-US')}-byte workspace limit. Use an evidence companion or reduce the declared output allowance before dispatch. Existing receipts stay retained.`,
    )
  if (state.receipts.length + pending.length + Number(!!proposed) > EXPERIMENT_LIMITS.receipts)
    issues.push('The retained and pending receipts fill this workspace. Use an evidence companion.')
  if (proposed && state.attempts.length >= 256)
    issues.push('The attempt history is full. Use an evidence companion.')
  return {
    usedBytes,
    pendingReceiptBytes,
    attemptBytes,
    receiptBytes,
    projectedBytes,
    limitBytes: EXPERIMENT_LIMITS.stateBytes,
    issues,
  }
}

/** A whole-workspace import is not permission to overwrite the only copy of a
 * failed run. Receipt admission has its own attempt-resolution transition. */
export function experimentHistoryIssues(
  previous: ExperimentWorkspaceState,
  incoming: ExperimentWorkspaceState,
): string[] {
  for (const key of ['receipts', 'attempts', 'revisions'] as const) {
    const kept = new Map((incoming[key] ?? []).map((row) => [row.id, experimentCanonical(row)]))
    if ((previous[key] ?? []).some((row) => kept.get(row.id) !== experimentCanonical(row)))
      return [
        'This import would replace retained execution history. Select “Import into a new companion” to preserve both records.',
      ]
  }
  return []
}

/** Explicit preparation after an input edit. Transitive dependencies still have
 * to be reviewed individually; refreshing a top-level reference cannot repair them. */
export function prepareExperimentRerun(
  spec: ExperimentSpec,
  artifacts: readonly ResearchArtifact[],
): ExperimentSpec {
  const inputs = spec.inputs.map((reference) => {
    const artifact = artifacts.find((a) => a.id === reference.id)
    if (!artifact) throw new Error(`Missing input: ${reference.id}`)
    return { id: artifact.id, revision: artifactRevision(artifact) }
  })
  return { ...spec, inputs }
}

export function queueExperimentAttempt(
  state: ExperimentWorkspaceState,
  experimentId: string,
  attemptId: string,
  now: number,
) {
  const spec = state.experiments.find((e) => e.id === experimentId)
  if (!spec || !id(attemptId) || state.attempts.some((a) => a.id === attemptId))
    throw new Error('Unknown experiment or duplicate attempt.')
  const action = selectExperimentActions({ ...state, ranking: null }, now).candidates.find(
    (a) => a.experimentId === experimentId,
  )
  if (!action?.eligible)
    throw new Error(action?.issues.join(' ') || 'This experiment is already reusable.')
  const capacity = experimentRetentionCapacity(state, experimentInput(spec, state.artifacts))
  if (capacity.issues.length) throw new Error(capacity.issues.join(' '))
  const next = {
    ...state,
    attempts: [
      ...state.attempts,
      {
        id: attemptId,
        experimentId,
        startedAt: now,
        inputKey: experimentInputKey(spec, state.artifacts),
        input: copy(experimentInput(spec, state.artifacts)),
        state: 'exported' as const,
        receiptId: null,
      },
    ],
  }
  if (!parseExperimentWorkspace(next))
    throw new Error('The experiment workspace is full; export it before adding more work.')
  return next
}

export function retainExperimentReceipt(
  state: ExperimentWorkspaceState,
  receipt: ExperimentReceipt,
) {
  if (state.receipts.some((r) => r.id === receipt.id)) {
    if (
      experimentCanonical(state.receipts.find((r) => r.id === receipt.id)) !==
      experimentCanonical(receipt)
    )
      throw new Error('A different receipt already has this ID.')
    return state
  }
  const key = experimentInputKey(receipt.spec, receipt.artifacts)
  const proposalKey = experimentInputKey({ ...receipt.spec, environment: null }, receipt.artifacts)
  const matching = [...state.attempts]
    .reverse()
    .find(
      (a) =>
        a.experimentId === receipt.experimentId &&
        (a.inputKey === key || a.inputKey === proposalKey) &&
        receipt.startedAt >= a.startedAt &&
        experimentCanonical(a.input) ===
          experimentCanonical(
            experimentInput(
              a.input.spec.environment ? receipt.spec : { ...receipt.spec, environment: null },
              receipt.artifacts,
            ),
          ) &&
        (a.state === 'exported' || a.state === 'unknown'),
    )
  const next = {
    ...state,
    receipts: [...state.receipts, receipt],
    attempts: state.attempts.map((a) =>
      a.id === matching?.id ? { ...a, state: 'completed' as const, receiptId: receipt.id } : a,
    ),
  }
  if (!parseExperimentWorkspace(next)) throw new Error('The receipt exceeds workspace limits.')
  return next
}

export function assessExperiment(receipt: ExperimentReceipt) {
  const invalid = (reason: string) => ({
    status: 'unassessed' as const,
    reason,
    value: null,
    compatibleHypothesisIds: [] as string[],
  })
  if (
    !receipt.spec.environment ||
    receipt.spec.environment.platform !== receipt.isolation.platform ||
    receipt.spec.environment.isolation !== receipt.isolation.kind ||
    receipt.spec.environment.runnerDigest !== receipt.isolation.runnerDigest
  )
    return invalid('The receipt does not match a bound execution environment.')
  // Preserve the actionable cause even when that limit stopped later seeds.
  for (const run of receipt.runs)
    for (const [role, observation] of [
      ['candidate', run.candidate],
      ['evaluator', run.evaluator],
    ] as const)
      if (observation?.limited)
        return invalid(
          `Seed ${run.seed} ${role} stopped: ${observation.limitReason ?? 'resource limit or cancellation; cause not recorded'}. This is not a mathematical rejection.`,
        )
  if (receipt.runs.length !== receipt.spec.seeds.length)
    return invalid('Not every declared seed completed.')
  const values: number[] = []
  for (const run of receipt.runs) {
    if (
      run.candidate.exitCode !== 0 ||
      run.candidate.signal ||
      run.candidate.limited ||
      !run.evaluator ||
      run.evaluator.exitCode !== 0 ||
      run.evaluator.signal ||
      run.evaluator.limited
    )
      return invalid('An execution failed or reached a resource limit.')
    let result: unknown
    try {
      result = JSON.parse(run.evaluator.stdout)
    } catch {
      return invalid('Evaluator returned invalid JSON.')
    }
    if (
      !obj(result) ||
      result.valid !== true ||
      !obj(result.metrics) ||
      !num(result.metrics[receipt.spec.metric.name]) ||
      !str(result.reason) ||
      !texts(result.checks, 1)
    )
      return invalid('Evaluator did not establish a valid comparison with explicit checks.')
    const value = result.metrics[receipt.spec.metric.name] as number
    if (value < receipt.spec.metric.minimum || value > receipt.spec.metric.maximum)
      return invalid('Evaluator metric is outside the declared range.')
    values.push(value)
  }
  const value = values.reduce((a, b) => a + b, 0) / values.length
  const compatibleHypothesisIds = receipt.spec.hypotheses
    .filter((h) => value >= h.prediction.minimum && value <= h.prediction.maximum)
    .map((h) => h.id)
  return {
    status:
      receipt.spec.evaluator.authority === 'task-author'
        ? ('evaluated' as const)
        : ('proposal-evaluated' as const),
    reason:
      compatibleHypothesisIds.length === 0
        ? 'No declared explanation fits this metric; retain the other-explanation possibility.'
        : compatibleHypothesisIds.length > 1
          ? 'The observed metric does not distinguish all remaining explanations.'
          : 'One declared prediction is compatible under this experiment’s assumptions.',
    value,
    compatibleHypothesisIds,
  }
}

/** Input currency says what ran, independently of who authored the evaluator
 * or whether its comparison passed. An unsuccessful current execution is still
 * current; relabeling it historical hides the failure rather than assessing it. */
export function experimentReceiptCurrency(
  spec: ExperimentSpec,
  artifacts: readonly ResearchArtifact[],
  receipt: ExperimentReceipt,
  now: number,
) {
  if (!spec.environment)
    return {
      status: 'unbound' as const,
      reason: 'Bind the current execution environment before comparing this receipt.',
    }
  if (
    receipt.experimentId !== spec.id ||
    experimentCanonical(experimentInput(receipt.spec, receipt.artifacts)) !==
      experimentCanonical(experimentInput(spec, artifacts))
  )
    return {
      status: 'different-inputs' as const,
      reason:
        'Historical execution: the retained contract or consumed inputs differ from the current ones.',
    }
  const issues = experimentInputIssues(spec, artifacts, now)
  return issues.length
    ? {
        status: 'stale-inputs' as const,
        reason: `The receipt matches the retained inputs, but they are no longer usable: ${issues.join('; ')}`,
      }
    : {
        status: 'current' as const,
        reason:
          'This execution matches the current bound contract and consumed inputs. Evaluator authority and result assessment are separate.',
      }
}

export function reusableExperiment(
  spec: ExperimentSpec,
  artifacts: readonly ResearchArtifact[],
  receipts: readonly ExperimentReceipt[],
  now: number,
): ExperimentReceipt | null {
  if (!spec.environment || experimentInputIssues(spec, artifacts, now).length) return null
  const exact = experimentCanonical(experimentInput(spec, artifacts))
  return (
    [...receipts]
      .reverse()
      .find(
        (r) =>
          r.experimentId === spec.id &&
          assessExperiment(r).status === 'evaluated' &&
          experimentCanonical(experimentInput(r.spec, r.artifacts)) === exact,
      ) ?? null
  )
}

export function planExperimentRepairs(state: ExperimentWorkspaceState, now: number) {
  return state.experiments.map((spec) => {
    const issues = experimentInputIssues(spec, state.artifacts, now)
    const reusable = reusableExperiment(spec, state.artifacts, state.receipts, now)
    const prior = [...state.receipts].reverse().find((r) => r.experimentId === spec.id)
    return {
      experimentId: spec.id,
      state: issues.length
        ? ('blocked-inputs' as const)
        : reusable
          ? ('reusable' as const)
          : prior
            ? ('rerun' as const)
            : ('unrun' as const),
      issues,
      receiptId: reusable?.id ?? null,
      affectedClaims: spec.context.nodeId ? [spec.context.nodeId] : [],
    }
  })
}

export function transferIssues(
  transfer: MechanismTransfer,
  state: ExperimentWorkspaceState,
): string[] {
  const out: string[] = []
  if (!state.artifacts.some((a) => a.id === transfer.sourceArtifactId))
    out.push('Source mechanism artifact is unavailable.')
  if (!state.experiments.some((e) => e.id === transfer.targetExperimentId))
    out.push('Target experiment is unavailable.')
  const target = state.experiments.find((e) => e.id === transfer.targetExperimentId)
  if (target && !target.inputs.some((r) => r.id === transfer.sourceArtifactId))
    out.push('The target must declare the source mechanism as a consumed input.')
  if (
    !transfer.mapping.length ||
    !transfer.preserved.length ||
    !transfer.changedConditions.length ||
    !transfer.failureTest.trim()
  )
    out.push('A mapping, preserved relationship, changed condition and failure test are required.')
  for (const row of transfer.mapping)
    if (row.sourceUnit !== row.targetUnit && !row.transform.trim())
      out.push(`Unit conversion is missing for ${row.source}.`)
  return out
}

/** Selection is an inspectable frontier, not a model probability. A pair counts
 * only when its numeric predictions are disjoint; this is conditional structure. */
export function selectExperimentActions(
  state: ExperimentWorkspaceState,
  now: number,
  selectDecision = createDecisionSelector(),
) {
  const repair = planExperimentRepairs(state, now)
  const candidates = state.experiments.map((spec) => {
    let separatedPairs = 0
    for (let i = 0; i < spec.hypotheses.length; i++)
      for (let j = i + 1; j < spec.hypotheses.length; j++) {
        const a = spec.hypotheses[i].prediction,
          b = spec.hypotheses[j].prediction
        if (a.maximum < b.minimum || b.maximum < a.minimum) separatedPairs++
      }
    const row = repair.find((r) => r.experimentId === spec.id)!
    const pending = state.attempts.some(
      (a) => a.experimentId === spec.id && (a.state === 'exported' || a.state === 'unknown'),
    )
    const transferProblems = state.transfers
      .filter((t) => t.targetExperimentId === spec.id)
      .flatMap((t) => transferIssues(t, state))
    return {
      experimentId: spec.id,
      separatedPairs,
      wallMs: spec.limits.wallMs * spec.seeds.length * 2,
      eligible:
        !pending && !transferProblems.length && (row.state === 'unrun' || row.state === 'rerun'),
      priority: row.state === 'rerun' ? 0 : 1,
      rule: `Changed valid inputs precede new work; prefer more disjoint prediction pairs, then a lower declared execution allowance. ${separatedPairs} pairs; ${spec.seeds.length} seeds × 2 processes × ${spec.limits.wallMs} ms.`,
      issues: [
        ...row.issues,
        ...transferProblems,
        ...(pending ? ['An unresolved attempt prevents duplicate execution.'] : []),
      ],
    }
  })
  const ready = candidates
    .filter((c) => c.eligible)
    .sort(
      (a, b) =>
        a.priority - b.priority ||
        b.separatedPairs - a.separatedPairs ||
        a.wallMs - b.wallMs ||
        a.experimentId.localeCompare(b.experimentId),
    )
  const frontier = ready.filter(
    (a) =>
      !ready.some(
        (b) =>
          b.experimentId !== a.experimentId &&
          b.priority <= a.priority &&
          b.separatedPairs >= a.separatedPairs &&
          b.wallMs <= a.wallMs &&
          (b.priority < a.priority || b.separatedPairs > a.separatedPairs || b.wallMs < a.wallMs),
      ),
  )
  let decision: DecisionRanking | null = null
  let rankingReason = state.ranking
    ? 'No eligible experiments to rank. Reusable results and unresolved attempts are excluded.'
    : 'Structural selection; quantitative ranking is off.'
  if (state.ranking && ready.length) {
    const declared = state.ranking
    const specs = ready.map((c) => state.experiments.find((e) => e.id === c.experimentId)!)
    const covered = specs.every(
      (spec) =>
        declared.model.tests.some((t) => t.id === spec.id) &&
        declared.bindings.some(
          (b) =>
            b.spec.id === spec.id &&
            experimentCanonical(b) === experimentCanonical(experimentInput(spec, state.artifacts)),
        ),
    )
    const resource = (spec: ExperimentSpec) =>
      experimentCanonical({
        limits: spec.limits,
        seeds: spec.seeds.length,
        candidate: spec.candidate.language,
        evaluator: spec.evaluator.language,
        environment: spec.environment,
      })
    const equal = specs.every((s) => resource(s) === resource(specs[0]!))
    if (!covered || !equal) {
      // Invalidate the closure too: returning to a prior declaration is a fresh admission.
      selectDecision(null)
      rankingReason = !covered
        ? 'Structural fallback: a quantitative partition is missing or its bound inputs changed.'
        : 'Structural fallback: quantitative ranking requires equal declared resource allowances and environments.'
    } else {
      decision = selectDecision(
        {
          ...declared.model,
          tests: declared.model.tests.filter((t) => ready.some((c) => c.experimentId === t.id)),
        },
        experimentCanonical(specs.map((s) => experimentInput(s, state.artifacts))),
      )
      rankingReason = decision
        ? 'Experimental decision value under your declared probabilities, utilities and equal resource allowances. No model confidence is used.'
        : 'Structural fallback: the quantitative model is invalid.'
    }
  } else selectDecision(null)
  const selected = decision
    ? ready.find((c) => c.experimentId === decision.selected)!
    : (ready[0] ?? null)
  return {
    selected: selected && decision ? { ...selected, rule: rankingReason } : selected,
    candidates,
    frontier,
    authority: decision
      ? ('declared-quantitative-model' as const)
      : ('declared-prediction-structure' as const),
    decision,
    rankingReason,
  }
}

/** Failed attempts retain their regime and counterevidence. They never blacklist
 * a mechanism in a different dataset, environment or set of conditions. */
export function experimentNegativeKnowledge(state: ExperimentWorkspaceState) {
  return state.receipts.flatMap((receipt) => {
    const assessment = assessExperiment(receipt)
    const hypotheses =
      assessment.status === 'unassessed'
        ? []
        : receipt.spec.hypotheses
            .filter((h) => !assessment.compatibleHypothesisIds.includes(h.id))
            .map((h) => h.id)
    if (assessment.status !== 'unassessed' && !hypotheses.length) return []
    return [
      {
        receiptId: receipt.id,
        experimentId: receipt.experimentId,
        kind:
          assessment.status === 'unassessed'
            ? ('execution-failure' as const)
            : ('incompatible-predictions' as const),
        reason:
          assessment.status === 'unassessed'
            ? assessment.reason
            : 'The retained evaluator metric falls outside these declared prediction ranges.',
        inputKey: experimentInputKey(receipt.spec, receipt.artifacts),
        hypotheses,
        conditions: receipt.spec.hypotheses.flatMap((h) => h.conditions),
      },
    ]
  })
}

export function proposeExperiment(
  state: ExperimentWorkspaceState,
  spec: ExperimentSpec,
  artifacts: ResearchArtifact[],
) {
  const ids = new Set(artifacts.map((a) => a.id))
  const next = {
    ...state,
    artifacts: [...state.artifacts.filter((a) => !ids.has(a.id)), ...artifacts],
    experiments: [...state.experiments.filter((e) => e.id !== spec.id), spec],
  }
  const parsed = parseExperimentWorkspace(next)
  if (!parsed) throw new Error('The proposed experiment is invalid or exceeds workspace limits.')
  return parsed
}

/** The model-facing observation surface is deliberately narrower than the
 * retained workspace. Evaluator code/data stay private before an execution. */
export function researchObservationView(
  state: ExperimentWorkspaceState,
  options: {
    memory?: boolean
    transfers?: boolean
    withholdArtifactIds?: string[]
    withholdReceiptIds?: string[]
  } = {},
) {
  const withheldArtifacts = new Set(options.withholdArtifactIds ?? []),
    withheldReceipts = new Set(options.withholdReceiptIds ?? [])
  for (const id of withheldArtifacts)
    if (!state.artifacts.some((a) => a.id === id))
      throw new Error(`Unknown withheld artifact: ${id}`)
  for (const id of withheldReceipts)
    if (!state.receipts.some((r) => r.id === id))
      throw new Error(`Unknown withheld observation: ${id}`)
  const privateInputs = new Set(
    state.experiments.flatMap((e) => [e.evaluator.artifactId, e.evaluatorDataId]),
  )
  const receipts =
    options.memory === false ? [] : state.receipts.filter((r) => !withheldReceipts.has(r.id))
  return {
    version: 1,
    intervention: {
      memory: options.memory !== false,
      transfers: options.transfers !== false,
      withholdArtifactIds: [...withheldArtifacts].sort(),
      withholdReceiptIds: [...withheldReceipts].sort(),
    },
    experiments: state.experiments,
    artifacts: state.artifacts.map((a) => ({
      id: a.id,
      kind: a.kind,
      scope: a.scope,
      revision: artifactRevision(a),
      content: privateInputs.has(a.id) || withheldArtifacts.has(a.id) ? null : a.content,
      visibility: privateInputs.has(a.id)
        ? 'evaluator-private'
        : withheldArtifacts.has(a.id)
          ? 'withheld-by-ablation'
          : 'available',
    })),
    observations: receipts.map((r) => ({
      id: r.id,
      experimentId: r.experimentId,
      inputDigest: r.inputDigest,
      assessment: assessExperiment(r),
      completedAt: r.completedAt,
    })),
    transfers: options.transfers === false ? [] : state.transfers,
    negativeKnowledge: experimentNegativeKnowledge({ ...state, receipts }),
  }
}

export const emptyExperimentWorkspace = (): ExperimentWorkspaceState => ({
  version: 1,
  draft: '',
  artifacts: [],
  experiments: [],
  receipts: [],
  attempts: [],
  transfers: [],
})

export function parseExperimentWorkspace(value: unknown): ExperimentWorkspaceState | null {
  try {
    experimentCanonical(value)
    if (
      !obj(value) ||
      value.version !== 1 ||
      typeof value.draft !== 'string' ||
      bytes(value.draft) > 512000 ||
      !list(value.artifacts, 0, EXPERIMENT_LIMITS.artifacts) ||
      !value.artifacts.every(parseResearchArtifact) ||
      !list(value.experiments, 0, EXPERIMENT_LIMITS.experiments) ||
      !value.experiments.every(parseExperimentSpec) ||
      !list(value.receipts, 0, EXPERIMENT_LIMITS.receipts) ||
      !value.receipts.every(parseExperimentReceipt) ||
      (value.links !== undefined &&
        (!list(value.links, 0, 64) ||
          !value.links.every(
            (link) =>
              obj(link) &&
              id(link.sessionId) &&
              str(link.title) &&
              ['source', 'companion'].includes(link.relation as string),
          ) ||
          !unique(value.links.map((link) => (link as { sessionId: string }).sessionId)))) ||
      !list(value.attempts, 0, 256) ||
      !list(value.transfers, 0, 64)
    )
      return null
    if (
      value.ranking != null &&
      (!obj(value.ranking) ||
        !parseDecisionModel(value.ranking.model) ||
        !list(value.ranking.bindings, 1, 64) ||
        !value.ranking.bindings.every(
          (b) =>
            obj(b) &&
            parseExperimentSpec(b.spec) &&
            list(b.artifacts, 4, EXPERIMENT_LIMITS.artifacts) &&
            b.artifacts.every(parseResearchArtifact),
        ) ||
        !unique(value.ranking.bindings.map((b) => (b as { spec: ExperimentSpec }).spec.id)))
    )
      return null
    if (
      value.revisions !== undefined &&
      (!list(value.revisions, 0, 128) ||
        !value.revisions.every(
          (r) =>
            obj(r) &&
            id(r.id) &&
            id(r.receiptId) &&
            (r.maxTokens === undefined ||
              (natural(r.maxTokens) &&
                Number(r.maxTokens) >= 1000 &&
                Number(r.maxTokens) <= 20000)) &&
            (r.continuationOf === undefined || id(r.continuationOf)) &&
            (r.review === undefined ||
              (obj(r.review) &&
                [
                  'finite-coverage',
                  'exact-counterexample',
                  'numerical-result',
                  'universal-argument',
                ].includes(r.review.kind as string) &&
                str(r.review.scope, 4000) &&
                typeof r.review.candidateOutput === 'boolean' &&
                list(r.review.publicArtifactIds, 0, 256) &&
                r.review.publicArtifactIds.every(id))) &&
            id(r.nodeId) &&
            str(r.claim, 16000) &&
            natural(r.startedAt) &&
            (r.opId === null || id(r.opId)) &&
            ['pending', 'completed', 'unknown', 'refused'].includes(r.state as string) &&
            list(r.produced, 0, 128) &&
            r.produced.every(id) &&
            (value.receipts as ExperimentReceipt[]).some((receipt) => receipt.id === r.receiptId),
        ) ||
        !unique((value.revisions as { id: string }[]).map((r) => r.id)))
    )
      return null
    for (const key of ['artifacts', 'experiments', 'receipts', 'attempts', 'transfers'] as const)
      if (!unique((value[key] as unknown[]).map((row) => (obj(row) ? String(row.id) : ''))))
        return null
    for (const a of value.attempts)
      if (
        !obj(a) ||
        !id(a.id) ||
        !id(a.experimentId) ||
        !natural(a.startedAt) ||
        !str(a.inputKey, 100) ||
        !obj(a.input) ||
        !parseExperimentSpec(a.input.spec) ||
        !list(a.input.artifacts, 4, EXPERIMENT_LIMITS.artifacts) ||
        !a.input.artifacts.every(parseResearchArtifact) ||
        (a.input.spec as ExperimentSpec).id !== a.experimentId ||
        experimentInputKey(
          a.input.spec as ExperimentSpec,
          a.input.artifacts as ResearchArtifact[],
        ) !== a.inputKey ||
        !['exported', 'completed', 'unknown', 'abandoned'].includes(a.state as string) ||
        (a.transport !== undefined && a.transport !== 'paired-local') ||
        (a.mathematics !== undefined &&
          (!obj(a.mathematics) ||
            !id(a.mathematics.experimentRevision) ||
            !['plan', 'target', 'package'].every((key) => {
              const ref = (a.mathematics as Record<string, unknown>)[key]
              return obj(ref) && id(ref.id) && id(ref.revision)
            }))) ||
        (a.receiptId !== null && !id(a.receiptId)) ||
        (a.state === 'completed' &&
          !value.receipts.some((r) => (r as ExperimentReceipt).id === a.receiptId))
      )
        return null
    for (const t of value.transfers)
      if (
        !obj(t) ||
        !id(t.id) ||
        !id(t.sourceArtifactId) ||
        !id(t.targetExperimentId) ||
        !str(t.sourceMechanism) ||
        !texts(t.preserved, 1) ||
        !texts(t.changedConditions, 1) ||
        !str(t.failureTest) ||
        !list(t.mapping, 1, 64) ||
        !t.mapping.every(
          (m) =>
            obj(m) &&
            ['source', 'target', 'sourceUnit', 'targetUnit'].every((k) => str(m[k])) &&
            typeof m.transform === 'string',
        )
      )
        return null
    return copy(value) as unknown as ExperimentWorkspaceState
  } catch {
    return null
  }
}
